import { Component, Inject, OnDestroy } from '@angular/core';
import { DocumentNode, ReviewerNode, User } from '@completion/models';
import { Store } from '@ngrx/store';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { selectEditMode, getCurrentDocument, getCurrentUser, getProjectEditorsAndAdmins } from '@completion/selectors';
import { FormControl } from '@angular/forms';
import { DocumentUpdateQueueService } from '../../../../core/services/document-update-queue.service';
import { MatDialog } from '@angular/material/dialog';
import { UserSelectDialogComponent, UserSelectDialogData } from '../../../../shared/user-select/user-select-dialog.component';
import { RemoveUserDialogComponent } from '../../../../shared/remove-user-dialog/remove-user-dialog.component';
import { persistNodeUpdateForNode } from '../node-persistence.util';
import { ProcedureDocument } from '@completion/models';
import { collectAssignedUserIds, filterUnassignedCandidates } from '@completion/utils';

@Component({
  selector: 'app-reviewer',
  templateUrl: './reviewer.component.html',
  styleUrls: ['./reviewer.component.scss'],
  standalone: false,
})
export class ReviewerComponent implements OnDestroy {
  documentNode: DocumentNode;
  editMode = false;
  currentUser: User = null as any;
  private currentDocument: ProcedureDocument | null = null;

  control = new FormControl('');
  private readonly destroy$ = new Subject();
  users$: Observable<User[]> = of([]);

  constructor(
    @Inject('documentNode') documentNode: DocumentNode,
    private store: Store,
    private updateQueue: DocumentUpdateQueueService,
    private dialog: MatDialog,
  ) {
    this.documentNode = documentNode;

    this.store
      .select(selectEditMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => (this.editMode = m));

    // subscribe to currentUser so we can prevent reassigning the current user
    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((u) => (this.currentUser = u as User));

    // Build exclusion id list from current document (authors + reviewers + approvers already assigned)
    this.store
      .select(getCurrentDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((doc) => (this.currentDocument = doc as ProcedureDocument));

    try {
      // use selector that returns only EDITOR/ADMIN users
      this.users$ = this.store.select(getProjectEditorsAndAdmins);
    } catch {
      this.users$ = of([]);
    }
  }

  get reviewer(): ReviewerNode | null {
    return (this.documentNode as ReviewerNode) || null;
  }

  get isAssignedCurrentUser(): boolean {
    const node = this.documentNode as ReviewerNode;
    const assignedId = node && node.user ? node.user.id || null : null;
    const currentId = this.currentUser ? this.currentUser.id || null : null;
    return assignedId != null && currentId != null && String(assignedId) === String(currentId);
  }

  selectUser(user: User) {
    // Prevent changing if the assigned user is the current logged-in user
    if (this.isAssignedCurrentUser) {
      this.control.setValue('');
      return;
    }

    const node = this.documentNode as ReviewerNode;
    const newContent = user?.name || (user as any)?.displayName || '';
    this.control.setValue('');

    // Use shared helper to clone, mutate and enqueue document update
    persistNodeUpdateForNode(this.store, this.updateQueue, node, (n: any) => {
      n.user = user;
      n.content = newContent;
    });
  }

  editReviewer() {
    if (this.isAssignedCurrentUser) {
      return;
    }
    const preselectedId = this.reviewer?.user?.id ?? undefined;
    // Read the latest current user and document from the store so exclusion is accurate
    this.store
      .select(getCurrentUser)
      .pipe(take(1))
      .subscribe((u) => {
        const myId = u && (u as any).id != null ? Number((u as any).id) : undefined;
        this.store
          .select(getCurrentDocument)
          .pipe(take(1))
          .subscribe((doc) => {
            // Prepare exclusion set (exclude all assigned users except the preselected one)
            const assignedIds = collectAssignedUserIds(doc);
            const excludeSet = new Set<number>(
              assignedIds.filter((id) => id !== (preselectedId == null ? undefined : Number(preselectedId))),
            );
            if (myId != null && myId !== (preselectedId == null ? undefined : Number(preselectedId))) {
              excludeSet.add(myId);
            }
            const excludeIds = Array.from(excludeSet);

            // Fetch the candidate list once and filter it locally to avoid relying on the dialog to do it
            this.users$.pipe(take(1)).subscribe((list) => {
              const filtered = filterUnassignedCandidates(list || [], doc);
              // Ensure we also remove the current user (unless preselected) from the filtered list
              const final = filtered.filter((uitem) => {
                const uid = (uitem as any).id;
                if (preselectedId != null && String(uid) === String(preselectedId)) return true; // keep preselected
                if (myId != null && String(uid) === String(myId)) return false;
                return true;
              });

              const data: UserSelectDialogData = {
                title: 'Select reviewer',
                users: final,
                preselectedUserId: preselectedId,
                excludeUserIds: excludeIds,
              };
              const ref = this.dialog.open(UserSelectDialogComponent, { width: '420px', data });
              ref
                .afterClosed()
                .pipe(take(1))
                .subscribe((user: User | undefined) => {
                  if (user) {
                    this.selectUser(user);
                  }
                });
            });
          });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  removeReviewer() {
    const ref = this.dialog.open(RemoveUserDialogComponent, {
      width: '500px',
      data: { title: 'Remove reviewer', message: 'Choose whether to unassign the user from this reviewer node or delete the entire node.' },
      panelClass: 'remove-user-panel',
    });
    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((res: any) => {
        if (!res) return;
        if (res === 'unassign') {
          const node = this.documentNode as ReviewerNode;
          persistNodeUpdateForNode(this.store, this.updateQueue, node, (n: any) => {
            n.user = null;
            n.content = '';
          });
          return;
        }
        if (res === 'delete') {
          this.store
            .select(getCurrentDocument)
            .pipe(take(1))
            .subscribe((doc) => {
              if (!doc) return;
              const cloned = JSON.parse(JSON.stringify(doc));
              const removeNodeByIdImmutable = (nodes: any[], id: number): { nodes: any[]; removed: boolean } => {
                if (!Array.isArray(nodes) || nodes.length === 0) return { nodes, removed: false };
                let removed = false;
                const next: any[] = [];
                for (const n of nodes) {
                  if (!n) continue;
                  if (n.id === id) {
                    removed = true;
                    continue;
                  }
                  let updatedChildren = n.children;
                  if (Array.isArray(n.children) && n.children.length) {
                    const res = removeNodeByIdImmutable(n.children, id);
                    if (res.removed) {
                      updatedChildren = res.nodes;
                      removed = true;
                    }
                  }
                  next.push(updatedChildren !== n.children ? { ...n, children: updatedChildren } : n);
                }
                return removed ? { nodes: next, removed: true } : { nodes, removed: false };
              };
              const { nodes: updatedNodes, removed } = removeNodeByIdImmutable(cloned.nodes || [], (this.documentNode as any).id);
              if (!removed) return;
              cloned.nodes = updatedNodes;
              try {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                (this.updateQueue as any).assignOrdinals ? (this.updateQueue as any).assignOrdinals(cloned) : null;
              } catch {}
              if ((this.updateQueue as any).queueImmediate) {
                (this.updateQueue as any).queueImmediate(cloned);
              } else {
                this.updateQueue.queue(cloned);
              }
            });
        }
      });
  }
}
